import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import {
  absRoute, ROUTE_ORGANIZATION, ROUTE_PROFILE
} from '../app-route.constants';
import { ConfigService } from '../core/config.service';
import { SessionService } from '../core/session/session.service';
import { CCOrganization } from '../model/organization/cc-org';

export const NAV_LEVEL_1 = 1;
export const NAV_LEVEL_2 = 2;
export type NAV_LEVEL = 1 | 2;

export interface NavLink {
  id: string;
  name: string;
  icon: string;
  path: string;
}

@Injectable()
export class NavigationService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly configService = inject(ConfigService);
  private readonly sessionService = inject(SessionService);


  private readonly titleSubject = new BehaviorSubject<string>('');
  private readonly titleSubscription: Observable<string> = this.titleSubject.asObservable();

  private readonly levelSubject = new BehaviorSubject<NAV_LEVEL>(NAV_LEVEL_2);
  private readonly levelSubscription: Observable<NAV_LEVEL> = this.levelSubject.asObservable();

  private readonly linksSubject: BehaviorSubject<NavLink[]> = new BehaviorSubject<NavLink[]>([]);
  private readonly linksSubscription: Observable<NavLink[]> = this.linksSubject.asObservable();

  private readonly organizationSubject = new BehaviorSubject<CCOrganization | undefined>(undefined);
  private readonly organizationSubscription: Observable<CCOrganization | undefined> = this.organizationSubject.asObservable();

  constructor() {
    this.router.events.pipe(
      // identify navigation end
      filter((event) => event instanceof NavigationEnd),
      // now query the activated route
      map(() => this.rootRoute(this.route)),
      filter((routeEvent: ActivatedRoute) => routeEvent.outlet === 'primary'),
    ).subscribe((routeEvent: ActivatedRoute) => {
      const fullRoute = this.router.routerState.snapshot.url;
      this.checkUrl(fullRoute, routeEvent.snapshot.paramMap);
    });
  }

  getOrganization(): Observable<CCOrganization | undefined> {
    return this.organizationSubscription;
  }

  getTitle(): Observable<string> {
    return this.titleSubscription;
  }

  setTitle(title: string) {
    this.titleSubject.next(title);
  }

  getLevel(): Observable<NAV_LEVEL> {
    return this.levelSubscription;
  }

  setLevel(level: NAV_LEVEL) {
    this.levelSubject.next(level);
  }

  getLinks(): Observable<NavLink[]> {
    return this.linksSubscription;
  }

  private checkUrl(url: string, _params: Params) {
    if (url.startsWith(absRoute(ROUTE_ORGANIZATION))) {
      this.organizationSubject.next(undefined);
      this.setLevel(NAV_LEVEL_1);
      this.titleService.setTitle(this.configService.getTitle());
      this.setTitle(this.configService.getTitle());
      this.buildAssestListsLinks();
    } else if (url.startsWith(absRoute(ROUTE_PROFILE))) {
      this.organizationSubject.next(undefined);
      this.setLevel(NAV_LEVEL_2);
      this.buildProfileLinks();
    } else {
      this.organizationSubject.next(undefined);
      this.setLevel(NAV_LEVEL_2);
    }
  }


  private buildProfileLinks() {
    this.linksSubject.next([]);
  }

  private readonly links: NavLink[] = [
    {
      id: ROUTE_ORGANIZATION,
      name: 'Organization',
      icon: 'hdmui:list',
      path: `${absRoute(ROUTE_ORGANIZATION)}`,
    }
  ];

  private buildAssestListsLinks() {
    const filteredLinks = this.links.filter(link => this.sessionService.checkRoute(link.path));
    this.linksSubject.next(filteredLinks);
  }

  private rootRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }
}
